import { jwtDecode } from 'jwt-decode';
import {
  clearAuthMode,
  isLegacyAuthMode,
  setLegacyAuthMode,
  setPortalAuthMode,
  syncAuthModeFromToken,
} from './loginAuth';

export type { AuthLoginMode } from './loginAuth';
export {
  syncAuthModeFromToken,
  isLegacyAuthMode,
  getLegacyAuthLoginUrl,
  setPortalAuthMode,
  setLegacyAuthMode,
  clearAuthMode,
} from './loginAuth';

export const LEGACY_LOGIN_ROLE = 1;

export interface AuthSession {
  portal?: string;
  grantId?: number;
  resourceId?: number;
  lobId?: number;
  role?: string;
  permissions?: string[];
}

interface JwtPayload {
  resourceId?: number;
  lobId?: number;
  userId?: number;
  sub?: string;
  portal?: string;
  grantId?: number;
  role?: string;
  permissions?: string[];
  exp?: number;
}

export const getAuthServiceUrl = (): string =>
  window.env.AUTH_SERVICE_URL || `${window.env.API_URL}/v1/authentication`;

/** Admin portal scope: RBAC resourceId is the LoB id (legacy login stores lobId on user). */
export const getAdminLobId = (): number | null => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}') as Record<
      string,
      unknown
    >;
    const fromUser = userDetails.lobId ?? userDetails.lob_id;
    if (fromUser != null && fromUser !== '') {
      return Number(fromUser);
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return null;

    const decoded = jwtDecode<JwtPayload>(token);
    if (decoded.lobId != null) return Number(decoded.lobId);
    if (decoded.portal === 'admin' && decoded.resourceId != null) {
      return Number(decoded.resourceId);
    }
    return null;
  } catch {
    return null;
  }
};

/** Route path segment for tenant APIs (legacy used roleResourceId; value is only for routing). */
export const getAdminTenantRouteScopeId = (): number | null => {
  const lobId = getAdminLobId();
  if (lobId != null) return lobId;

  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}') as Record<
      string,
      unknown
    >;
    const roleResourceId = userDetails.roleResourceId;
    return roleResourceId != null ? Number(roleResourceId) : null;
  } catch {
    return null;
  }
};

export const getStoredPermissions = (): string[] => {
  try {
    const raw = localStorage.getItem('permissions');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const getStoredRole = (): string | null => localStorage.getItem('role');

export const hasPermission = (feature: string): boolean => {
  if (isLegacyAuthMode()) return true;
  const permissions = getStoredPermissions();
  if (!permissions.length) return false;
  if (permissions.includes('*')) return true;
  if (permissions.includes(feature)) return true;
  if (feature.endsWith('.view') && permissions.includes('*.view')) return true;
  return false;
};

export const persistAuthSession = (token: string, session?: AuthSession) => {
  localStorage.setItem('accessToken', token);
  localStorage.removeItem('refreshToken');
  setPortalAuthMode();
  if (session?.role) {
    localStorage.setItem('role', session.role);
  }
  if (session?.permissions) {
    localStorage.setItem('permissions', JSON.stringify(session.permissions));
  }

  let lobId = session?.lobId ?? session?.resourceId;
  if (lobId == null) {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      lobId = decoded?.lobId ?? (decoded?.portal === 'admin' ? decoded?.resourceId : undefined);
    } catch {
      // RBAC addon only; legacy login stores userDetails separately.
    }
  }
  if (lobId != null) {
    try {
      const existing = JSON.parse(localStorage.getItem('userDetails') || '{}') as Record<string, unknown>;
      localStorage.setItem('userDetails', JSON.stringify({
        ...existing,
        lobId,
        resourceId: lobId,
      }));
    } catch {
      localStorage.setItem('userDetails', JSON.stringify({ lobId, resourceId: lobId }));
    }
  }
};

export const persistLegacyAuthSession = (
  accessToken: string,
  refreshToken: string,
  user?: unknown
) => {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
  setLegacyAuthMode();
  if (user) {
    localStorage.setItem('userDetails', JSON.stringify(user));
  }
};

export const clearAuthSession = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userDetails');
  localStorage.removeItem('permissions');
  localStorage.removeItem('role');
  clearAuthMode();
};
